import type { ReactNode } from 'react';
import type { TCEmployeeExecution } from 'src/api/generated';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import { EmployeeWorkspaceType } from 'src/api/generated/models/EmployeeWorkspaceType';
import { toDisplayFromKey } from 'src/modules/shared/utils/formatting/text.util';
import { getTriggerMetadata } from 'src/modules/automations/utils/executions/timeline/trigger-metadata.util';

export interface TriggerVisual {
  displayName: string;
  icon?: ReactNode;
  colorClass: string; // e.g., 'text-blue-600'
  bgColorClass: string; // e.g., 'bg-blue-50 border-blue-100'
  count: number;
  resourceType?: string;
  resourceAction?: string;
  badgeVariant: ComponentVariant;
  triggerNames: string[];
  isMulti: boolean;
  taskObjective?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringProp = (value: unknown, key: string): string | undefined => {
  if (!isRecord(value)) return undefined;
  const v = value[key];
  return typeof v === 'string' ? v : undefined;
};

const getRecordLabel = (value: Record<string, unknown>): string | undefined =>
  ['objective', 'title', 'name']
    .map((key) => value[key])
    .find((candidate): candidate is string =>
      Boolean(typeof candidate === 'string' && candidate)
    );

const getTaskDataObjective = (
  value: Record<string, unknown>
): string | undefined =>
  getRecordLabel(value) ??
  (isRecord(value.task) ? getRecordLabel(value.task) : undefined);

/** Extracts a task objective from direct or legacy nested trigger data. */
const getTaskObjective = (value: unknown): string | undefined => {
  if (!isRecord(value)) return undefined;
  return (
    getTaskDataObjective(value) ??
    (isRecord(value.data) ? getTaskDataObjective(value.data) : undefined)
  );
};

/**
 * Build a visual summary for a set of triggers, reusing timeline metadata
 */
interface MinimalTrigger {
  triggerId?: string;
  data?: unknown;
}

export function buildTriggerVisual(
  triggers: MinimalTrigger[],
  translate?: I18nContextValue['t']
): TriggerVisual | null {
  if (!Array.isArray(triggers) || triggers.length === 0) return null;

  if (triggers.length === 1) {
    const trigger = triggers[0];
    const triggerId =
      typeof trigger.triggerId === 'string' ? trigger.triggerId : '';
    const resourceAction = getStringProp(trigger.data, 'resourceAction');
    const resourceType = getStringProp(trigger.data, 'resourceType');
    const meta = getTriggerMetadata(triggerId, resourceAction, translate);
    const lowerId = triggerId.toLowerCase();
    const lowerAction = (resourceAction ?? '').toLowerCase();
    const isDueOrExpired =
      lowerAction.includes('expired') ||
      lowerAction.includes('overdue') ||
      lowerAction.includes('due') ||
      lowerId.includes('expired') ||
      lowerId.includes('overdue') ||
      lowerId.includes('due');
    const taskObjective = isDueOrExpired
      ? getTaskObjective(trigger.data)
      : undefined;
    return {
      displayName: meta.displayName,
      icon: meta.icon,
      colorClass: meta.color,
      bgColorClass: meta.bgColor,
      count: 1,
      resourceAction,
      resourceType,
      badgeVariant: meta.badgeVariant,
      triggerNames: [meta.displayName],
      isMulti: false,
      taskObjective,
    };
  }

  // Multiple triggers - show first type and count
  const metas = triggers.map((t) =>
    getTriggerMetadata(
      typeof t.triggerId === 'string' ? t.triggerId : '',
      undefined,
      translate
    )
  );
  const displayNames: string[] = metas.map((m) => m.displayName);
  const unique = Array.from(new Set(displayNames));
  const first = unique[0];
  const count = unique.length;
  const primary = metas[0];
  return {
    displayName:
      count === 1
        ? first
        : translate?.('automations.aiAgents.metadata.additionalTriggers', {
            name: first,
            count: count - 1,
          }) ?? `${first} y ${count - 1} más`,
    icon: primary.icon,
    colorClass: primary.color,
    bgColorClass: primary.bgColor,
    count: triggers.length,
    badgeVariant: primary.badgeVariant,
    triggerNames: displayNames,
    isMulti: true,
  };
}

/**
 * Map EmployeeWorkspaceType to a localized display label.
 */
export function workspaceTypeToDisplay(
  workspaceType?: EmployeeWorkspaceType | string,
  translate?: I18nContextValue['t']
): string {
  if (!workspaceType) return '';
  const key = String(workspaceType);
  const map: Record<string, string> = {
    [EmployeeWorkspaceType.FUNNEL_STATUS]:
      translate?.('automations.aiAgents.metadata.workspace.funnelStatus') ??
      'Estado de Embudo',
  };
  return map[key] ?? toDisplayFromKey(key);
}

/**
 * Preferred localized execution title for cards and pages.
 */
export function getExecutionDisplayTitle(
  execution: TCEmployeeExecution,
  translate?: I18nContextValue['t']
): string {
  const tv = buildTriggerVisual(execution.inputs.triggers, translate);
  if (tv?.displayName) return tv.displayName;
  return workspaceTypeToDisplay(execution.inputs.workspaceType, translate);
}
